import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × REMBRANDT: Warm introspection through digital precision
// Soft gradients, warm glows, radial shading
export function VasarelyRembrandtMode({ brand }: ModeRendererProps) {
  return (
    <g>
      {/* Warm background glow */}
      <defs>
        <radialGradient id="rembrandt-warmth" cx="0.5" cy="0.45">
          <stop offset="0%" stopColor={brand.amber} stopOpacity="0.3" />
          <stop offset="50%" stopColor="#8B4513" stopOpacity="0.15" />
          <stop offset="100%" stopColor={brand.deep} stopOpacity="0.05" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="794" height="1123" fill="url(#rembrandt-warmth)" />

      <g transform="translate(397 540)">
        {/* Soft radial light field */}
        {Array.from({ length: 20 }).map((_, i) => {
          const r = 40 + i * 18;
          const opacity = 0.25 - i * 0.01;
          return (
            <circle
              key={i}
              cx="0"
              cy="0"
              r={r}
              fill="none"
              stroke={i % 2 === 0 ? brand.amber : "#CD853F"}
              strokeWidth="2"
              opacity={opacity}
            />
          );
        })}

        {/* Warm optical grid with depth */}
        {Array.from({ length: 10 }).map((_, i) =>
          Array.from({ length: 10 }).map((_, j) => {
            const x = (i - 4.5) * 50;
            const y = (j - 4.5) * 50;
            const dist = Math.sqrt(x * x + y * y);
            const glow = Math.max(0, 1 - dist / 300);
            const warmth = glow * 0.6;
            
            return (
              <circle
                key={`${i}-${j}`}
                cx={x}
                cy={y}
                r={8 + glow * 8}
                fill={warmth > 0.3 ? brand.amber : brand.aqua}
                opacity={warmth}
              />
            );
          })
        )}

        {/* Contemplative geometric forms */}
        {Array.from({ length: 6 }).map((_, i) => {
          const angle = (i * Math.PI) / 3;
          const r = 160;
          const x = Math.cos(angle) * r;
          const y = Math.sin(angle) * r;
          
          return (
            <g key={i}>
              <ellipse
                cx={x}
                cy={y}
                rx="45"
                ry="60"
                fill={brand.amber}
                opacity="0.2"
              />
              <ellipse
                cx={x}
                cy={y}
                rx="30"
                ry="40"
                fill={brand.violet}
                opacity="0.15"
              />
            </g>
          );
        })}

        {/* Soft intersecting lines (introspective structure) */}
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i * Math.PI) / 6;
          return (
            <line
              key={i}
              x1="0"
              y1="0"
              x2={Math.cos(angle) * 280}
              y2={Math.sin(angle) * 280}
              stroke={brand.amber}
              strokeWidth="1.5"
              opacity="0.2"
            />
          );
        })}

        {/* Warm trust nodes */}
        {[
          { x: 0, y: 0, r: 20 },
          { x: -120, y: -100, r: 12 },
          { x: 120, y: -100, r: 12 },
          { x: -120, y: 100, r: 12 },
          { x: 120, y: 100, r: 12 },
        ].map((node, i) => (
          <g key={i}>
            <circle cx={node.x} cy={node.y} r={node.r} fill={brand.amber} opacity="0.4" />
            <circle cx={node.x} cy={node.y} r={node.r * 1.5} fill="none" stroke={brand.amber} strokeWidth="2" opacity="0.3" />
            <circle cx={node.x} cy={node.y} r={node.r * 2} fill="none" stroke={brand.amber} strokeWidth="1" opacity="0.2" />
          </g>
        ))}

        {/* Depth layering */}
        <rect x="-220" y="-200" width="440" height="400" fill="none" stroke={brand.amber} strokeWidth="1" opacity="0.2" />
        <rect x="-180" y="-160" width="360" height="320" fill="none" stroke={brand.aqua} strokeWidth="1" opacity="0.25" />
      </g>

      {/* Warm introspection note */}
      <text x="397" y="1000" textAnchor="middle" fontFamily="serif" fontSize="11" fill={brand.amber} opacity="0.6">
        warme vertrouwdheid
      </text>
    </g>
  );
}
